import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Eye, Edit, Trash2, Search, ChevronUp, ChevronDown, X, User, Mail, Phone, Calendar, Shield, Activity } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import ExportButtons from './ExportButtons';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

const CustomerListManager = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter only non-trainer users (customers/clients)
        const usersList = data.users || data; // Handle both response formats
        const customersOnly = Array.isArray(usersList) 
          ? usersList.filter(user => user.role !== 'trainer' && user.role !== 'admin')
          : [];
        setCustomers(customersOnly);
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast({ title: 'Error', description: 'Failed to load customers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleViewCustomer = async (customerId) => {
    setLoadingCustomer(true);
    setShowModal(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/users/${customerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedCustomer(data);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load customer details',
          variant: 'destructive'
        });
        setShowModal(false);
      }
    } catch (error) {
      console.error('Error fetching customer:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while loading customer details',
        variant: 'destructive'
      });
      setShowModal(false);
    } finally {
      setLoadingCustomer(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedCustomer(null);
  };

  const openDeleteDialog = (customer) => {
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!customerToDelete) return;
    
    const customerId = customerToDelete.user_id || customerToDelete.id || customerToDelete._id;
    setDeleteDialogOpen(false);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/users/${customerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Customer deleted successfully' });
        setCustomerToDelete(null);
        fetchCustomers();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete customer', variant: 'destructive' });
    }
  };

  const handleStatusToggle = async (customerId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      
      const response = await fetch(`${API_BASE}/api/admin/users/${customerId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        // Update local state immediately for instant UI feedback
        setCustomers(prevCustomers => 
          prevCustomers.map(customer => {
            if ((customer.id || customer._id || customer.user_id) === customerId) {
              return {
                ...customer,
                status: newStatus,
                is_verified: newStatus === 'active',
                email_verified: newStatus === 'active'
              };
            }
            return customer;
          })
        );
        
        toast({ title: 'Success', description: `Customer status updated to ${newStatus}` });
      } else {
        toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  // Filter and sort customers
  const filteredCustomers = customers
    .filter(customer => {
      const searchLower = searchTerm.toLowerCase();
      return (
        customer.name?.toLowerCase().includes(searchLower) ||
        customer.email?.toLowerCase().includes(searchLower) ||
        customer.phone?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle special cases
      if (sortField === 'id') {
        aVal = a.id || a._id;
        bVal = b.id || b._id;
      }
      if (sortField === 'created_at') {
        aVal = new Date(a.created_at || 0);
        bVal = new Date(b.created_at || 0);
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="w-4 h-4 text-gray-300" />;
    return sortDirection === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-blue-600" />
      : <ChevronDown className="w-4 h-4 text-blue-600" />;
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading customers...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Customer List</h2>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">Manage and monitor all customers</p>
      </div>

      {/* Search */}
      <div className="mb-4 sm:mb-6">
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full"
          />
        </div>
      </div>

      {/* Export Buttons */}
      <ExportButtons 
        data={filteredCustomers}
        columns={[
          { header: 'ID', accessor: (row) => row.id || row._id || 'N/A' },
          { header: 'Name', accessor: (row) => row.name || 'N/A' },
          { header: 'Email', accessor: (row) => row.email || 'N/A' },
          { header: 'Phone', accessor: (row) => row.phone || 'N/A' },
          { header: 'Role', accessor: (row) => row.role || 'N/A' },
          { header: 'Created At', accessor: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A' }
        ]}
        filename="customer-list"
      />

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-max">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b">
                <tr>
                  <th 
                    className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort('id')}
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="whitespace-nowrap">ID</span>
                      <SortIcon field="id" />
                    </div>
                  </th>
                  <th 
                    className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="whitespace-nowrap">Name</span>
                      <SortIcon field="name" />
                    </div>
                  </th>
                  <th className="hidden md:table-cell px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <span className="whitespace-nowrap">Phone</span>
                  </th>
                  <th 
                    className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="whitespace-nowrap">Email</span>
                      <SortIcon field="email" />
                    </div>
                  </th>
                  <th 
                    className="hidden lg:table-cell px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="whitespace-nowrap">Register Date</span>
                      <SortIcon field="created_at" />
                    </div>
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <span className="whitespace-nowrap">Status</span>
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <span className="whitespace-nowrap">Action</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
                {filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-3 sm:px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                      No customers found
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((customer, index) => (
                    <tr key={customer.id || customer._id} className="hover:bg-gray-50 dark:bg-gray-700">
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {index + 1}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{customer.name || 'N/A'}</div>
                      </td>
                      <td className="hidden md:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {customer.phone || 'N/A'}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <div className="max-w-[150px] sm:max-w-none truncate">{customer.email || 'N/A'}</div>
                      </td>
                      <td className="hidden lg:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <Badge 
                          className={`text-xs ${
                            customer.status === 'active' || customer.is_verified || customer.email_verified
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {customer.status === 'active' || customer.is_verified || customer.email_verified ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-1 sm:gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewCustomer(customer.id || customer._id)}
                            className="hover:bg-gray-100 dark:hover:bg-gray-700 p-1 sm:p-2"
                            title="View customer"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className={`p-1 sm:p-2 ${
                              customer.status === 'active' || customer.is_verified || customer.email_verified
                                ? 'text-green-600 hover:text-green-700 hover:bg-green-50'
                                : 'text-gray-600 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                            onClick={() => handleStatusToggle(
                              customer.id || customer._id || customer.user_id, 
                              customer.status || (customer.is_verified || customer.email_verified ? 'active' : 'inactive')
                            )}
                            title={`Toggle status (currently ${customer.status === 'active' || customer.is_verified || customer.email_verified ? 'Active' : 'Inactive'})`}
                          >
                            <Activity className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 sm:p-2"
                            onClick={() => openDeleteDialog(customer)}
                            title="Delete customer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Stats Footer */}
          <div className="px-4 sm:px-6 py-3 bg-gray-50 dark:bg-gray-700 border-t text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center justify-between">
              <span>
                Showing <span className="font-semibold text-gray-900 dark:text-white">{filteredCustomers.length}</span> of <span className="font-semibold text-gray-900 dark:text-white">{customers.length}</span> customers
              </span>
              {searchTerm && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSearchTerm('')}
                  className="text-red-600 hover:text-red-700"
                >
                  Clear search
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Customer Detail Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <User className="w-6 h-6" />
                <h2 className="text-2xl font-bold dark:text-white">Customer Details</h2>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-white dark:bg-gray-800/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {loadingCustomer ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading customer details...</p>
                  </div>
                </div>
              ) : selectedCustomer ? (
                <div className="space-y-6">
                  {/* Customer Name */}
                  <div className="text-center pb-4 border-b">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedCustomer.name || 'Unknown Customer'}</h3>
                  </div>

                  {/* Customer Info Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Email */}
                    <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Email</p>
                        <p className="text-sm text-gray-900 dark:text-white break-words">{selectedCustomer.email || 'N/A'}</p>
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <Phone className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Phone</p>
                        <p className="text-sm text-gray-900 dark:text-white">{selectedCustomer.phone || 'Not provided'}</p>
                      </div>
                    </div>

                    {/* Role */}
                    <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Shield className="w-5 h-5 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Role</p>
                        <p className="text-sm text-gray-900 dark:text-white capitalize">{selectedCustomer.role || 'customer'}</p>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                        <Activity className="w-5 h-5 text-yellow-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</p>
                        <Badge 
                          className={
                            selectedCustomer.is_verified || selectedCustomer.email_verified
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                              : 'bg-gray-100 text-gray-800'
                          }
                        >
                          {selectedCustomer.is_verified || selectedCustomer.email_verified ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </div>

                    {/* Registration Date */}
                    <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                        <Calendar className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Registered</p>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {selectedCustomer.created_at 
                            ? new Date(selectedCustomer.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })
                            : 'N/A'
                          }
                        </p>
                      </div>
                    </div>

                    {/* User ID */}
                    <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <User className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">User ID</p>
                        <p className="text-xs text-gray-700 dark:text-gray-300 font-mono break-all">
                          {selectedCustomer.id || selectedCustomer._id || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Account Information</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Email Verified:</span>
                        <Badge className={selectedCustomer.email_verified ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'}>
                          {selectedCustomer.email_verified ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Account Status:</span>
                        <Badge className={selectedCustomer.suspended ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'}>
                          {selectedCustomer.suspended ? 'Suspended' : 'Active'}
                        </Badge>
                      </div>
                      {selectedCustomer.updated_at && (
                        <div className="flex justify-between items-center py-2">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Last Updated:</span>
                          <span className="text-sm text-gray-900 dark:text-white">
                            {new Date(selectedCustomer.updated_at).toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 dark:text-gray-400">Customer details not available</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-700 px-6 py-4 border-t flex justify-end">
              <Button onClick={closeModal} className="bg-blue-600 hover:bg-blue-700">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{customerToDelete?.name || customerToDelete?.email}</strong>? 
              This action cannot be undone and will remove all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCustomerToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Yes, Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CustomerListManager;
